import { MetricCardType } from "@/components/MetricCard/MetricCard.types";
import { Path } from "@/router";
import { toFormat } from "@/lib/dateHelpers";
import { Person } from "@/types";
import { buildPATStatusForNonMembers } from "./utils";
import { getConsolidatedPATStatusLabel } from "./pat-status";
import { buildPlayingAbilityTestPathAwareTooltip, isPatCredentialStillMetForCurrentPath } from "./pathAwareExpiration";

export type PATData = NonNullable<Person["prospectProgram"]>["playingAbilityTestStatus"] | null | undefined;

const PAT_VALUE = "Playing Ability Test";
const PAT_ICON = "sports_golf";

export type BuildPATMetricOptions = {
  onClick?: () => void;
  scrollToId?: string;
  pathAwareExpiration?: boolean;
  person?: Person;
};

function isPatOutcomePassed(patData: NonNullable<PATData>): boolean {
  return patData.completed === true && patData.status?.toLowerCase() === "passed" && !patData.activeSuspension;
}

export function isPatPassed(
  patData: PATData,
  options?: Pick<BuildPATMetricOptions, "pathAwareExpiration" | "person">
): boolean {
  if (!patData) return false;
  if (!isPatOutcomePassed(patData)) return false;

  if (!patData.expired) return true;

  if (options?.pathAwareExpiration && options.person) {
    return isPatCredentialStillMetForCurrentPath(options.person, patData);
  }

  return false;
}

function buildLabel(status: string, dateString: string | null): string {
  return dateString ? `${status}\nDate: ${dateString}` : status;
}

export function buildPATMetric(patData: PATData, options?: BuildPATMetricOptions): MetricCardType<Path> {
  if (!patData) {
    return {
      label: getConsolidatedPATStatusLabel("not_started"),
      value: PAT_VALUE,
      iconName: PAT_ICON,
      ...(options?.onClick && { onClick: options.onClick }),
      ...(options?.scrollToId && { scrollToId: options.scrollToId })
    };
  }

  const pathAware = !!options?.pathAwareExpiration && !!options.person;
  const stillMetDespiteExpiry =
    pathAware && patData.expired && isPatCredentialStillMetForCurrentPath(options.person!, patData);

  if (isPatPassed(patData, options)) {
    const dateString = patData.startDate ? toFormat(patData.startDate) : null;
    const pathAwareTooltip = stillMetDespiteExpiry
      ? buildPlayingAbilityTestPathAwareTooltip({
          stillMet: true,
          expiredDate: patData.expirationDate,
          person: options.person
        })
      : undefined;

    return {
      label: buildLabel(getConsolidatedPATStatusLabel("passed"), dateString),
      value: PAT_VALUE,
      iconName: PAT_ICON,
      variant: "success",
      ...(pathAwareTooltip && { tooltip: pathAwareTooltip }),
      ...(options?.onClick && { onClick: options.onClick }),
      ...(options?.scrollToId && { scrollToId: options.scrollToId })
    };
  }

  const patStatus = buildPATStatusForNonMembers(patData);
  const isQualifying = patStatus.value === getConsolidatedPATStatusLabel("qualifying");
  const isInProgress = patStatus.value === getConsolidatedPATStatusLabel("in_progress");
  const isInProgressOrQualifying = isQualifying || isInProgress;
  const forceWarn = isInProgress || (isQualifying && !options?.pathAwareExpiration);
  const variant = forceWarn ? "warn" : patStatus.variant;
  let tooltip;
  if (isInProgressOrQualifying) {
    tooltip = {
      supportingText: "Qualifying score required to begin Associate Program\nPassing score required for Member Election"
    };
  } else if (pathAware && patData.expired) {
    tooltip = buildPlayingAbilityTestPathAwareTooltip({
      stillMet: false,
      expiredDate: patData.expirationDate,
      person: options.person
    });
  } else if (
    patStatus.value !== getConsolidatedPATStatusLabel("failed") &&
    patStatus.value !== getConsolidatedPATStatusLabel("expired")
  ) {
    tooltip = patStatus.tooltip;
  }

  let dateString: string | null = null;
  if (patData.activeSuspension?.startDate) {
    dateString = toFormat(patData.activeSuspension.startDate);
  } else if (patData.expired && patData.expirationDate) {
    dateString = toFormat(patData.expirationDate);
  } else if (patData.startDate) {
    dateString = toFormat(patData.startDate);
  }

  return {
    label: buildLabel(patStatus.value, dateString),
    value: PAT_VALUE,
    iconName: PAT_ICON,
    ...(variant && { variant }),
    ...(tooltip && { tooltip }),
    ...(options?.onClick && { onClick: options.onClick }),
    ...(options?.scrollToId && { scrollToId: options.scrollToId })
  };
}
